import React, { useState } from "react";
import { ArrowRight, CheckCircle2, FileDown } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { pick } from "../lib/useHomeContent";

/**
 * White paper capture module — appears when the CMS has no KPIs loaded yet.
 * Reuses POST /api/leads (the same endpoint that handles contact requests),
 * tagging entries with the "White Paper · IIEAS" dimension so they're easy
 * to filter from the admin panel.
 *
 * All visible copy is read from the CMS (content.whitepaper), with fallbacks
 * baked-in so the component never renders blank.
 */
const SECTORS_ES = [
  "Farmacéutica",
  "ONG / Asociación de Pacientes",
  "Dispositivos Médicos",
  "Institución Pública",
  "Aseguradora / GMM",
  "Otro",
];
const OTHER_ES = "Otro";

const SECTORS_EN = [
  "Pharmaceutical",
  "NGO / Patient Org",
  "Medical Devices",
  "Public Institution",
  "Insurer / GMM",
  "Other",
];
const OTHER_EN = "Other";

const FALLBACK_ES = {
  eyebrow: "Mientras curamos los indicadores públicos",
  title: "Recibe el último white paper del IIEAS.",
  description:
    "Suscríbete para obtener nuestra investigación más reciente sobre acceso basado en valor y resultados reportados por el paciente en LATAM. Sin spam, sin reenvíos.",
  cta: "Enviar white paper",
};
const FALLBACK_EN = {
  eyebrow: "While our public KPIs are being curated",
  title: "Get the latest Convergen IIEAS white paper.",
  description:
    "Sign up to receive our most recent open research on value-based access and patient-reported outcomes in LATAM. No spam, no resharing.",
  cta: "Send white paper",
};

export default function WhitePaperCapture({ whitepaper }) {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [sector, setSector] = useState("");
  const [sectorOther, setSectorOther] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const fb = lang === "en" ? FALLBACK_EN : FALLBACK_ES;
  const wp = whitepaper || {};
  const eyebrow = pick(wp, "eyebrow", lang, fb.eyebrow);
  const title = pick(wp, "title", lang, fb.title);
  const description = pick(wp, "description", lang, fb.description);
  const ctaLabel = lang === "en"
    ? (wp.cta_en || fb.cta)
    : (wp.cta_es || fb.cta);
  const dateLabel = wp.date || "";

  const sectors = lang === "en" ? SECTORS_EN : SECTORS_ES;
  const otherLabel = lang === "en" ? OTHER_EN : OTHER_ES;
  const isOther = sector === otherLabel;
  const effectiveSector = isOther ? sectorOther.trim() : sector;
  const canSubmit =
    /.+@.+\..+/.test(email) && effectiveSector && !submitting;

  const labels = lang === "en" ? {
    emailLabel: "Institutional email",
    sectorLabel: "Sector",
    sectorPlaceholder: "Pick a sector…",
    otherLabel: "Tell us your sector",
    otherPlaceholder: "Type your sector…",
    successTitle: "Request received.",
    successBody: "We'll send the white paper to your inbox shortly. Welcome to Convergen Insiders.",
    privacy: "By submitting you accept Convergen's confidentiality policy. We never share your data.",
  } : {
    emailLabel: "Correo institucional",
    sectorLabel: "Sector",
    sectorPlaceholder: "Elige un sector…",
    otherLabel: "Cuéntanos tu sector",
    otherPlaceholder: "Escribe tu sector…",
    successTitle: "Solicitud recibida.",
    successBody: "Te enviaremos el white paper a tu correo en breve. Bienvenida(o) a Convergen Insiders.",
    privacy: "Al enviar aceptas la política de confidencialidad de Convergen. No compartimos tus datos.",
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await api.post("/leads", {
        name: email.split("@")[0],
        email,
        organization: "",
        sector: effectiveSector,
        dimension: "White Paper · IIEAS",
        message: lang === "en"
          ? "Subscriber requested the latest IIEAS white paper from the Home page."
          : "Suscriptor solicitó el último white paper del IIEAS desde el Home.",
        language: lang,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mt-16 bg-cerulean/10 border border-cerulean p-10 md:p-12 text-center" data-testid="whitepaper-success">
        <CheckCircle2 className="w-12 h-12 text-cerulean mx-auto" strokeWidth={1.4} />
        <h3 className="display-heading text-white text-2xl md:text-3xl mt-6">{labels.successTitle}</h3>
        <p className="mt-4 text-white/75 text-base md:text-lg max-w-xl mx-auto leading-relaxed">{labels.successBody}</p>
      </div>
    );
  }

  return (
    <div className="mt-16 bg-white/5 border border-white/15 p-8 md:p-12" data-testid="whitepaper-capture">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.22em] uppercase text-cerulean">
            <FileDown className="w-4 h-4" strokeWidth={1.5} />
            {eyebrow}
            {dateLabel && <span className="text-white/40 ml-2">· {dateLabel}</span>}
          </div>
          <h3 className="display-heading text-white text-2xl md:text-3xl mt-6" data-testid="whitepaper-title">{title}</h3>
          <p className="mt-5 text-white/65 text-sm md:text-base leading-relaxed">{description}</p>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 grid grid-cols-1 md:grid-cols-12 gap-3">
          <label className="block md:col-span-6">
            <span className="block text-[10px] font-mono tracking-[0.18em] uppercase text-white/50 mb-1.5">{labels.emailLabel}</span>
            <Input
              data-testid="whitepaper-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="ejemplo@dominio.com"
              className="rounded-none h-11 bg-ink border-white/25 text-white placeholder:text-white/30 focus:border-cerulean focus-visible:ring-cerulean"
            />
          </label>
          <label className="block md:col-span-6">
            <span className="block text-[10px] font-mono tracking-[0.18em] uppercase text-white/50 mb-1.5">{labels.sectorLabel}</span>
            <select
              data-testid="whitepaper-sector"
              value={sector}
              onChange={(e) => {
                setSector(e.target.value);
                if (e.target.value !== otherLabel) setSectorOther("");
              }}
              required
              className="w-full h-11 px-3 bg-ink border border-white/25 text-white text-sm focus:border-cerulean focus:outline-none rounded-none cursor-pointer hover:border-cerulean/60 transition-colors"
            >
              <option value="" className="text-ink">{labels.sectorPlaceholder}</option>
              {sectors.map((s) => <option key={s} value={s} className="text-ink">{s}</option>)}
            </select>
          </label>

          {isOther && (
            <label className="block md:col-span-12" data-testid="whitepaper-sector-other-wrapper">
              <span className="block text-[10px] font-mono tracking-[0.18em] uppercase text-white/50 mb-1.5">{labels.otherLabel}</span>
              <Input
                data-testid="whitepaper-sector-other"
                type="text"
                value={sectorOther}
                onChange={(e) => setSectorOther(e.target.value)}
                placeholder={labels.otherPlaceholder}
                required
                autoFocus
                className="rounded-none h-11 bg-ink border-white/25 text-white placeholder:text-white/30 focus:border-cerulean focus-visible:ring-cerulean"
              />
            </label>
          )}

          {error && (
            <div className="md:col-span-12 text-sm text-red-300" data-testid="whitepaper-error">{error}</div>
          )}

          <div className="md:col-span-12 flex items-center justify-between gap-4 flex-col sm:flex-row mt-1">
            <p className="text-[11px] font-mono tracking-[0.14em] uppercase text-white/35 leading-relaxed max-w-md">
              {labels.privacy}
            </p>
            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="whitepaper-submit"
              className="bg-cerulean hover:bg-white hover:text-ink text-white rounded-none h-11 px-6 text-[12px] tracking-[0.18em] uppercase font-medium transition-colors disabled:opacity-40"
            >
              {submitting ? "…" : ctaLabel} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
